import {useEffect, useRef, useState} from 'react';
import type {SyntheticEvent} from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {Dialog} from 'shells/dialog/dialog';
import {TranscribeButton} from 'shells/live-transcription/transcribe-button';

import {analyticsClient} from '../../analytics-client';

import {useGetPowerAiLiveNoteMonthlyUsage} from '../power-ai/power-ai-usage-popover/graphql/hooks';

import {TranscribeInfoPopover} from './transcribe-info-popover';
import {useRequestTranscribeToken} from './use-request-transcribe-token';

export interface TranscribeButtonContainerProps {
    handleTranscribe: (arg1: string, arg2: boolean) => void;
    onStartTranscribe?: () => void;
    onEndTranscribe?: () => void;
    stopTranscribe?: (arg1: () => void) => void;
    eip?: any;
    isRoutedLink?: boolean;
    location: 'timeline' | 'activity-modal' | 'email-composer' | 'ai-dashboard';
}

function trackTranscribeFinishEvent(location: string, duration?: number | null): void {
    analyticsClient.track(analyticsClient.EVENTS.TRACK_TRANSCRIPTION_FINISHED, {
        location: location,
        duration,
    });
}

export function TranscribeButtonContainer(props: TranscribeButtonContainerProps) {
    const buttonRef = useRef<HTMLButtonElement | null | undefined>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null | undefined>(null);
    const [isFirstTranscription, setIsFirstTranscription] = useState<boolean>(true);
    const [isShowingPopover, setIsShowingPopover] = useState<boolean>(false);
    const [isTranscribing, setIsTranscribing] = useState<boolean>(false);

    const transcribeStartTimestamp = useRef<number | null>(null);

    const requestToken = useRequestTranscribeToken();

    const usage = useGetPowerAiLiveNoteMonthlyUsage().monthlyUsage;

    const {stopTranscribe} = props;

    useEffect(() => {
        if (isTranscribing && stopTranscribe) {
            stopTranscribe(() => {
                setIsTranscribing(false);
            });
        }

        return () => {
            if (!isTranscribing && stopTranscribe) {
                stopTranscribe(() => {});
            }
        };
    }, [isTranscribing, stopTranscribe]);

    useEffect(() => {
        const handleEipStopWorthyEvent = () => {
            setIsTranscribing(false);
        };

        if (props.eip) {
            props.eip.container.on('EipDidClose', handleEipStopWorthyEvent);
            props.eip.container.on('input', handleEipStopWorthyEvent);
        }

        return () => {
            if (props.eip) {
                props.eip.container.off('EipDidClose', handleEipStopWorthyEvent);
                props.eip.container.off('input', handleEipStopWorthyEvent);
            }
        };
    }, [props.eip]);

    const handleStop = () => {
        setIsTranscribing(false);
        const duration = transcribeStartTimestamp.current
            ? Date.now() - Number(transcribeStartTimestamp.current)
            : null;
        transcribeStartTimestamp.current = null;

        trackTranscribeFinishEvent(props.location, duration);

        if (props.onEndTranscribe) {
            props.onEndTranscribe();
        }
    };

    useEffect(() => {
        if (isTranscribing) {
            setIsLoading(true);
            setError(null);

            requestToken()
                .then((response) => {
                    const token =
                        response &&
                        response.data &&
                        response.data.transcribeToken &&
                        response.data.transcribeToken.token;

                    if (!token) {
                        setIsLoading(false);
                        setIsTranscribing(false);
                        setError('There was a problem starting transcription.');

                        return;
                    }

                    transcribeStartTimestamp.current = Date.now();
                    SpeechToText.start(
                        token,
                        props.handleTranscribe,
                        // startCallback
                        () => {
                            setIsLoading(false);
                            if (props.onStartTranscribe) {
                                props.onStartTranscribe();
                            }
                        },
                        // stopCallback
                        () => {
                            setIsTranscribing(false);
                        },
                        // errorCallback
                        (errorMessage: string | undefined) => {
                            setIsTranscribing(false);
                            setIsLoading(false);
                            setError(errorMessage || 'An error occurred while transcribing.');
                        }
                    );
                })
                .catch((e) => {
                    setIsLoading(false);
                    setIsTranscribing(false);
                    setError(
                        safelyGetGraphQLErrorMessage(e) ||
                            'There was a problem starting transcription.'
                    );
                });
        }

        return () => {
            if (isTranscribing) {
                SpeechToText.stop();
                handleStop();
            }
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isTranscribing]);

    const handleClick = (e: SyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (
            isFirstTranscription &&
            usage &&
            typeof usage.allTimeUsage === 'number' &&
            usage.allTimeUsage <= 0
        ) {
            setIsShowingPopover(true);
        } else if (!isLoading) {
            setIsTranscribing(!isTranscribing);
        }
    };

    const handleConfirm = () => {
        if (isFirstTranscription) {
            setIsTranscribing(true);
            setIsFirstTranscription(false);
            setIsShowingPopover(false);

            return;
        }

        setIsTranscribing(true);
    };

    useEffect(() => {
        if (!props.eip) {
            return;
        }

        props.eip.options.saveOnBlur = !isShowingPopover;

        return () => {
            if (!isShowingPopover && props.eip) {
                props.eip.options.saveOnBlur = true;
            }
        };
    }, [isShowingPopover, props.eip]);

    return (
        <>
            <TranscribeButton
                getRef={(node) => {
                    buttonRef.current = node;
                }}
                isLoading={isLoading}
                onClick={handleClick}
                isTranscribing={isTranscribing && !isLoading}
            />
            {isShowingPopover ? (
                <TranscribeInfoPopover
                    anchor={buttonRef}
                    isFirstTranscription={isFirstTranscription}
                    onConfirm={handleConfirm}
                />
            ) : undefined}
            {error ? (
                <Dialog
                    anchor={buttonRef.current}
                    location='top'
                    question={error}
                    primaryText='Close'
                    onPrimary={() => setError(null)}
                    primaryVariant='secondary'
                />
            ) : undefined}
        </>
    );
}
